import { useTreatment } from '@splitsoftware/splitio-react';

export interface UseHarnessFlagOptions {
  flagKey: string;
  splitKey?: SplitIO.SplitKey;
  attributes?: SplitIO.Attributes;
}

export interface UseHarnessFlagReturn {
  treatment: string;
  isReady: boolean;
  isTimedOut: boolean;
  enabled: boolean;
  /**
   * True once the treatment is trustworthy: the SDK is ready (from network
   * or local cache) or has timed out (treatment stays 'control'/off).
   * Until then, treatment is 'control' and enabled is false, so consumers
   * that must not flash the flag-off state should wait for this.
   */
  isResolved: boolean;
}

/**
 * Evaluate a single Harness feature flag via the Split.io React SDK.
 *
 * Returns 'control' (the SDK default) until the client is ready.
 * When the SDK key is not configured (local dev, Docker Compose),
 * the provider uses 'localhost' mode and returns 'control' for all
 * flags, so consumers degrade gracefully (flag off).
 *
 * Pass `splitKey` to evaluate the flag for a specific user (e.g. after
 * login). If omitted, the factory's default key (anonymous UUID) is used.
 *
 * Session account attributes (`accountId`, `accountUri`, etc.) are bound
 * on the default client via `HarnessProviderWrapper` / `SplitFactoryProvider`.
 * A keyed client (`splitKey`) gets its own attribute bag — provider attributes
 * do not copy over. Pass `attributes` here when a rule targets on account
 * context for that key (e.g. `accountUri` on a per-candidate `splitKey`).
 */
const useHarnessFlag = ({
  flagKey,
  splitKey,
  attributes,
}: UseHarnessFlagOptions): UseHarnessFlagReturn => {
  const { treatment, isReady, isReadyFromCache, isTimedout } = useTreatment({
    name: flagKey,
    splitKey,
    attributes,
  });

  return {
    treatment,
    isReady,
    isTimedOut: isTimedout,
    enabled: treatment === 'on',
    isResolved: isReady || isReadyFromCache || isTimedout,
  };
};

export default useHarnessFlag;
